// src/contexts/AuthContext.jsx - COMPLETE UPDATED VERSION
import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db, storage } from '../services/firebase';
import { doc, onSnapshot, collection, addDoc, query, where, getDocs, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  registerVendor, 
  loginVendor, 
  logoutVendor, 
  resetPassword as resetPasswordService,
  getVendorProfile,
  updateVendorProfile
} from '../services/authService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [vendorProfile, setVendorProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [registrationStep, setRegistrationStep] = useState(1);
  const [registrationData, setRegistrationData] = useState({});

  // Local storage for categories and brands during registration
  const [localCategories, setLocalCategories] = useState([]);
  const [localBrands, setLocalBrands] = useState([]);

  // User-specific categories and brands after login
  const [userCategories, setUserCategories] = useState([]);
  const [userBrands, setUserBrands] = useState([]);

  // Helper function to handle errors
  const handleError = (error) => {
    console.error(error);
    let errorMessage = 'An error occurred. Please try again.';
    
    if (error.code === 'auth/email-already-in-use') {
      errorMessage = 'This email is already registered. Please login instead.';
    } else if (error.code === 'auth/weak-password') {
      errorMessage = 'Password should be at least 6 characters long.';
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'Please enter a valid email address.';
    } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      errorMessage = 'Invalid email or password.';
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    setError(errorMessage);
    return errorMessage;
  };

  // Upload image to Firebase Storage
  const uploadImageToStorage = async (file, path) => {
    if (!file) return null;

    try {
      const timestamp = Date.now();
      const fileName = `${path}/${timestamp}_${file.name}`;
      const storageRef = ref(storage, fileName);

      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw new Error('Failed to upload image');
    }
  };

  // Add local category during registration
  const addLocalCategory = async (categoryData) => {
    try {
      let imageUrl = categoryData.image;

      // Upload image if it's a file
      if (categoryData.image && typeof categoryData.image === 'object') {
        imageUrl = await uploadImageToStorage(categoryData.image, 'temp_categories');
      }

      const newCategory = {
        id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: categoryData.name,
        description: categoryData.description || `${categoryData.name} components and parts`,
        image: imageUrl || 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=400&h=300&fit=crop',
        isActive: true,
        order: localCategories.length + 1,
        isLocal: true,
        createdAt: new Date()
      };

      setLocalCategories(prev => [...prev, newCategory]);
      return newCategory;
    } catch (error) {
      console.error('Error adding local category:', error);
      throw error;
    }
  };

  // Add local brand during registration
  const addLocalBrand = async (brandData) => {
    try {
      let logoUrl = brandData.logo;

      // Upload logo if it's a file
      if (brandData.logo && typeof brandData.logo === 'object') {
        logoUrl = await uploadImageToStorage(brandData.logo, 'temp_brands');
      }

      const newBrand = {
        id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: brandData.name,
        description: brandData.description || `${brandData.name} automotive brand`,
        logo: logoUrl || `https://via.placeholder.com/120x60/f0f0f0/666666?text=${brandData.name.replace(/\s+/g, '+')}`,
        website: brandData.website || '',
        isActive: true,
        isLocal: true,
        createdAt: new Date()
      };

      setLocalBrands(prev => [...prev, newBrand]);
      return newBrand;
    } catch (error) {
      console.error('Error adding local brand:', error);
      throw error;
    }
  };

  // Remove local category
  const removeLocalCategory = (categoryId) => {
    setLocalCategories(prev => prev.filter(cat => cat.id !== categoryId));
  };

  // Remove local brand
  const removeLocalBrand = (brandId) => {
    setLocalBrands(prev => prev.filter(brand => brand.id !== brandId));
  };

  // Store local categories and brands to Firebase after successful registration
  const storeUserCategoriesAndBrands = async (userId) => {
    try {
      console.log('Storing user categories and brands for user:', userId);
      
      // Store locally created categories for this user in the existing categories collection
      if (localCategories.length > 0) {
        console.log('Storing categories:', localCategories);
        const categoriesPromises = localCategories.map(category => {
          const categoryData = {
            ...category,
            createdBy: userId, // Use createdBy instead of userId to work with existing rules
            isUserCreated: true, // Flag to identify user-created categories
            createdAt: new Date(),
            updatedAt: new Date(),
            isLocal: false // Remove local flag when storing to Firebase
          };
          // Remove the local id when storing to Firebase
          delete categoryData.id;
          return addDoc(collection(db, 'categories'), categoryData);
        });
        await Promise.all(categoriesPromises);
        console.log('Categories stored successfully');
      }

      // Store locally created brands for this user in the existing brands collection
      if (localBrands.length > 0) {
        console.log('Storing brands:', localBrands);
        const brandsPromises = localBrands.map(brand => {
          const brandData = {
            ...brand,
            createdBy: userId, // Use createdBy instead of userId to work with existing rules
            isUserCreated: true, // Flag to identify user-created brands
            createdAt: new Date(),
            updatedAt: new Date(),
            isLocal: false // Remove local flag when storing to Firebase
          };
          // Remove the local id when storing to Firebase
          delete brandData.id;
          return addDoc(collection(db, 'brands'), brandData);
        });
        await Promise.all(brandsPromises);
        console.log('Brands stored successfully');
      }

      // Clear local storage after successful storage
      setLocalCategories([]);
      setLocalBrands([]);
      
    } catch (error) {
      console.error('Error storing user categories and brands:', error);
      // Don't throw error here to prevent registration failure
    }
  };

  // Fetch user-specific categories and brands from existing collections
  const fetchUserCategoriesAndBrands = async (userId) => {
    try {
      console.log('Fetching user categories and brands for user:', userId);
      
      // Fetch all categories and filter user-created ones
      const categoriesRef = collection(db, 'categories');
      const categoriesSnap = await getDocs(categoriesRef);
      const userCategoriesList = categoriesSnap.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(category => category.createdBy === userId && category.isUserCreated === true)
        .map(category => ({
          ...category,
          isUserSpecific: true
        }));

      // Fetch all brands and filter user-created ones
      const brandsRef = collection(db, 'brands');
      const brandsSnap = await getDocs(brandsRef);
      const userBrandsList = brandsSnap.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(brand => brand.createdBy === userId && brand.isUserCreated === true)
        .map(brand => ({
          ...brand,
          isUserSpecific: true
        }));

      setUserCategories(userCategoriesList);
      setUserBrands(userBrandsList);
      
      console.log('User categories loaded:', userCategoriesList.length);
      console.log('User brands loaded:', userBrandsList.length);
      
    } catch (error) {
      console.error('Error fetching user categories and brands:', error);
      setUserCategories([]);
      setUserBrands([]);
    }
  };

  // Add category for logged-in user using existing collection
  const addUserCategory = async (categoryData) => {
    if (!currentUser) {
      throw new Error('User must be logged in to add categories');
    }

    try {
      let imageUrl = categoryData.image;

      // Upload image if it's a file
      if (categoryData.image && typeof categoryData.image === 'object') {
        imageUrl = await uploadImageToStorage(categoryData.image, `categories/${currentUser.uid}`);
      }

      const newCategoryData = {
        name: categoryData.name,
        description: categoryData.description || `${categoryData.name} components and parts`,
        image: imageUrl || 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=400&h=300&fit=crop',
        isActive: true,
        order: userCategories.length + 1,
        createdBy: currentUser.uid, // Use createdBy instead of userId
        isUserCreated: true, // Flag to identify user-created categories
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, 'categories'), newCategoryData);
      
      const newCategory = {
        id: docRef.id,
        ...newCategoryData,
        isUserSpecific: true
      };

      setUserCategories(prev => [...prev, newCategory]);
      return newCategory;
    } catch (error) {
      console.error('Error adding user category:', error);
      throw error;
    }
  };

  // Add brand for logged-in user
  const addUserBrand = async (brandData) => {
    if (!currentUser) {
      throw new Error('User must be logged in to add brands');
    }

    try {
      let logoUrl = brandData.logo;

      // Upload logo if it's a file
      if (brandData.logo && typeof brandData.logo === 'object') {
        logoUrl = await uploadImageToStorage(brandData.logo, `user_brands/${currentUser.uid}`);
      }

      const newBrandData = {
        name: brandData.name,
        description: brandData.description || `${brandData.name} automotive brand`,
        logo: logoUrl || `https://via.placeholder.com/120x60/f0f0f0/666666?text=${brandData.name.replace(/\s+/g, '+')}`,
        website: brandData.website || '',
        isActive: true,
        userId: currentUser.uid,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, 'userBrands'), newBrandData);
      
      const newBrand = {
        id: docRef.id,
        ...newBrandData,
        isUserSpecific: true
      };

      setUserBrands(prev => [...prev, newBrand]);
      return newBrand;
    } catch (error) {
      console.error('Error adding user brand:', error);
      throw error;
    }
  };

  // Registration step handlers
  const updateRegistrationData = (data) => {
    setRegistrationData(prevData => ({
      ...prevData,
      ...data
    }));
  };

  const nextRegistrationStep = () => {
    setRegistrationStep(prev => prev + 1);
  };

  const previousRegistrationStep = () => {
    setRegistrationStep(prev => Math.max(1, prev - 1));
  };

  const resetRegistration = () => {
    setRegistrationStep(1);
    setRegistrationData({});
    setLocalCategories([]);
    setLocalBrands([]);
  };

  // Auth functions
  async function register() {
    try {
      setError('');
      const result = await registerVendor(registrationData);
      setCurrentUser(result.user);
      setVendorProfile(result.profile);
      
      // Store locally created categories and brands for this user
      await storeUserCategoriesAndBrands(result.user.uid);
      
      // Fetch user-specific data
      await fetchUserCategoriesAndBrands(result.user.uid);
      
      resetRegistration();
      return result;
    } catch (error) {
      handleError(error);
      throw error;
    }
  }

  async function login(email, password) {
    try {
      setError('');
      const result = await loginVendor(email, password);
      setCurrentUser(result.user);
      setVendorProfile(result.profile);
      
      // Fetch user-specific categories and brands
      await fetchUserCategoriesAndBrands(result.user.uid);
      
      return result;
    } catch (error) {
      handleError(error);
      throw error;
    }
  }

  async function logout() {
    try {
      setError('');
      await logoutVendor();
      setCurrentUser(null);
      setVendorProfile(null);
      
      // Clear all local data
      setLocalCategories([]);
      setLocalBrands([]);
      setUserCategories([]);
      setUserBrands([]);
    } catch (error) {
      handleError(error);
      throw error;
    }
  }

  async function resetPassword(email) {
    try {
      setError('');
      await resetPasswordService(email);
    } catch (error) {
      handleError(error);
      throw error;
    }
  }

  async function updateProfile(profileData) {
    try {
      setError('');
      if (!currentUser) throw new Error('No user logged in');
      
      const updatedProfile = await updateVendorProfile(currentUser.uid, profileData);
      setVendorProfile(updatedProfile);
      return updatedProfile;
    } catch (error) {
      handleError(error);
      throw error;
    }
  }

  // Get all categories (system + user-created + local)
  const getAllCategories = () => {
    // For non-logged-in users during registration, return local categories
    if (!currentUser) {
      return localCategories;
    }
    
    // For logged-in users, return user-specific categories only
    // System categories will be fetched separately by parent components
    return userCategories;
  };

  // Get all brands (system + user-created + local)
  const getAllBrands = () => {
    // For non-logged-in users during registration, return local brands
    if (!currentUser) {
      return localBrands;
    }
    
    // For logged-in users, return user-specific brands only
    // System brands will be fetched separately by parent components
    return userBrands;
  };

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setLoading(false);

      if (!user) {
        setVendorProfile(null);
        setUserCategories([]);
        setUserBrands([]);
        return;
      }

      // Fetch user-specific categories and brands
      await fetchUserCategoriesAndBrands(user.uid);

      // Instead of one-time fetch, listen to real-time updates of vendor profile
      const vendorRef = doc(db, 'vendors', user.uid);
      const unsubscribeVendor = onSnapshot(vendorRef, (doc) => {
        if (doc.exists()) {
          setVendorProfile(doc.data());
        } else {
          console.log('No vendor profile found!');
          setVendorProfile(null);
        }
      }, (error) => {
        console.error('Error fetching vendor profile:', error);
        handleError(error);
      });

      return () => unsubscribeVendor();
    });

    return () => unsubscribeAuth();
  }, []);

  const value = {
    currentUser,
    vendorProfile,
    loading,
    error,
    register,
    login,
    logout,
    resetPassword,
    updateProfile,
    registrationStep,
    registrationData,
    updateRegistrationData,
    nextRegistrationStep,
    previousRegistrationStep,
    resetRegistration,
    
    // Local categories and brands (for registration)
    localCategories,
    localBrands,
    addLocalCategory,
    addLocalBrand,
    removeLocalCategory,
    removeLocalBrand,
    
    // User-specific categories and brands (for logged-in users)
    userCategories,
    userBrands,
    addUserCategory,
    addUserBrand,
    
    // Helper functions
    getAllCategories,
    getAllBrands,
    uploadImageToStorage
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}